import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    HostListener,
    OnDestroy,
    Signal,
    computed,
    effect,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';

import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ProgressSpinner } from 'primeng/progressspinner';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Tooltip } from 'primeng/tooltip';

import { BcmBookingsApiService } from '@bcmApiServices/bcm-bookings.api-service';
import { BoatApiService } from '@bcmApiServices/boat.api-service';
import { TenantRelationsApiService } from '@bcmApiServices/tenant-relations.api-service';
import { BcmBooking } from '@shared/models/bcm-booking';
import { ElectricMeterAssignment } from '@shared/models/electric-meter-assignment';
import { BcmNavigationService } from '@modules/bcm/bcm-navigation.service';
import { buildNewInvoiceNavigationParams } from '@bcm-work-flows/@form-widgets/positions/form-widget-positions-invoice-navigation.util';
import { PayByCashDialogComponent } from '@sharedComponents/dialogs/pay-by-cash-dialog/pay-by-cash-dialog.component';
import { AppNotificationService } from '@core/services/app-notification.service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { TranslationModule } from '@core/translation/translation.module';
import { U2bConfirmDialogComponent } from '@u2bUi/dialog/u2b-confirm-dialog.component';
import { U2bDialogComponent } from '@u2bUi/dialog/u2b-dialog.component';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';
import { U2bSegmentedToggleComponent, U2bSegmentedToggleOption } from '@u2bUi/segmented-toggle/u2b-segmented-toggle.component';
import { U2bWizardStep, U2bWizardStepperComponent } from '@u2bUi/wizard-stepper/u2b-wizard-stepper.component';
import { OnboardingTourComponent } from './components/onboarding-tour/onboarding-tour.component';
import { BookingV2BerthOption, BookingV2BoatOption, BookingV2ElectricMeterAssignment, BookingV2PersonKind, BookingV2PersonOption } from './dtos/booking-v2.dtos';
import { BookingV2CommitService } from './services/booking-v2-commit.service';
import { BookingV2BerthProductService } from './services/booking-v2-berth-product.service';
import { BookingV2RelationPricingService } from './services/booking-v2-relation-pricing.service';
import { BookingV2ServicePricingService } from './services/booking-v2-service-pricing.service';
import { mapBookingToWizardState, toProposedMeterAssignment } from './services/booking-v2-load.mapper';
import { selectBelegPositions } from './utils/booking-v2-beleg';
import { relationRuleRequirements, toBerthOption, toBoatOption, toCompanyOption, toPersonOption, toRelationProducts } from './utils/booking-v2-option.mappers';
import { BookingV2ContractDialogComponent } from './contract/booking-v2-contract-dialog.component';
import { BookingV2DateRange, BookingV2Facade, BookingV2Step, BookingV2Type } from './services/booking-v2.facade';
import { AdvancedStepComponent } from './steps/advanced-step/advanced-step.component';
import { BerthStepComponent } from './steps/berth-step/berth-step.component';
import { DateRangeStepComponent } from './steps/date-range-step/date-range-step.component';
import { PersonStepComponent } from './steps/person-step/person-step.component';
import { RechnungsversandStepComponent } from './steps/rechnungsversand-step/rechnungsversand-step.component';
import { SummaryStepComponent } from './steps/summary-step/summary-step.component';

export interface BookingV2DialogData {
    // Edit mode: load this booking and seed every step from it (the prefill
    // fields below are then ignored).
    bookingId?: number;
    bookingType?: BookingV2Type;
    boat?: BookingV2BoatOption;
    berths?: readonly BookingV2BerthOption[];
    person?: BookingV2PersonOption;
    dateRange?: Partial<BookingV2DateRange>;
    // Suppress the one-shot boat→owner auto-fill in the person step (the caller
    // has already decided the booking person).
    skipOwnerDefault?: boolean;
    /** Optional Auftrags-Vorauswahl (#6739). */
    prefilledOrderId?: number;
}

@Component({
    selector: 'booking-v2-dialog',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        Button,
        ButtonIcon,
        ButtonLabel,
        ProgressSpinner,
        ToggleSwitch,
        Tooltip,
        TranslationModule,
        U2bDialogComponent,
        U2bIconButtonComponent,
        U2bSegmentedToggleComponent,
        U2bWizardStepperComponent,
        DateRangeStepComponent,
        BerthStepComponent,
        PersonStepComponent,
        AdvancedStepComponent,
        RechnungsversandStepComponent,
        SummaryStepComponent,
        BookingV2ContractDialogComponent,
    ],
    providers: [BookingV2Facade, BookingV2CommitService, BookingV2RelationPricingService, BookingV2ServicePricingService, BookingV2BerthProductService],
    templateUrl: './booking-v2-dialog.component.html',
    // Passthrough host: `<u2b-dialog>` is the panel. `rounded-[inherit]` keeps
    // the inherit chain (`.p-dialog` → `.p-dialog-content` → here → u2b-dialog)
    // intact so the panel clips at the modal's corner radius.
    host: { class: 'contents rounded-[inherit]' },
})
export class BookingV2DialogComponent implements OnDestroy {

    readonly facade = inject(BookingV2Facade);
    readonly commitService = inject(BookingV2CommitService);
    // Eagerly instantiated so dynamic relation prices re-evaluate for the whole
    // dialog lifetime — incl. edit, which lands on the summary (person step unmounted).
    private readonly relationPricing = inject(BookingV2RelationPricingService);
    // Same lifetime reason: dynamic service prices must re-evaluate even while the
    // services tab is unmounted (it is destroyed on advanced-step tab switch).
    private readonly servicePricing = inject(BookingV2ServicePricingService);
    // Same lifetime reason: a berth picked in the berth step must seed its Produkte
    // as Leistungen no matter which step is currently mounted (#6734).
    private readonly berthProducts = inject(BookingV2BerthProductService);
    private readonly dialogRef = inject(DynamicDialogRef);
    private readonly config = inject(DynamicDialogConfig<BookingV2DialogData>);
    private readonly overlay = inject(Overlay);
    private readonly bookingsApi = inject(BcmBookingsApiService);
    private readonly boatApi = inject(BoatApiService);
    private readonly tenantRelationsApi = inject(TenantRelationsApiService);
    private readonly bcmNavigation = inject(BcmNavigationService);
    private readonly matDialog = inject(MatDialog);
    private readonly notifications = inject(AppNotificationService);
    private readonly destroyRef = inject(DestroyRef);

    // Boat whose open meters are already proposed, so re-selecting the same boat
    // does not refetch (null = none / proposals cleared).
    private lastProposedBoatId: number | null = null;

    readonly data = this.config.data;

    // True while the booking is fetched in edit mode (body shows a spinner).
    readonly loading = signal<boolean>(false);

    readonly bookingTypeOptions: U2bSegmentedToggleOption<BookingV2Type>[] = [
        { value: 'booking', icon: 'event', labelKey: 'bookingV2.type.booking' },
        { value: 'reservation', icon: 'assignment', labelKey: 'bookingV2.type.reservation' },
    ];

    private readonly stepDefinitions: Readonly<Record<BookingV2Step, U2bWizardStep>> = {
        date:     { id: 'date',     icon: 'event',      labelKey: 'bookingV2.stepLabels.date' },
        berth:    { id: 'berth',    icon: 'anchor',     labelKey: 'bookingV2.stepLabels.berth' },
        person:   { id: 'person',   icon: 'person',     labelKey: 'bookingV2.stepLabels.person' },
        advanced: { id: 'advanced', icon: 'tune',       labelKey: 'bookingV2.stepLabels.advanced' },
        rechnungsversand: { id: 'rechnungsversand', icon: 'receipt_long', labelKey: 'bookingV2.stepLabels.rechnungsversand' },
        summary:  { id: 'summary',  icon: 'fact_check', labelKey: 'bookingV2.stepLabels.summary' },
    };

    // Reservation mode reuses the same steps but relabels date/person for the
    // reservation context ("Reservierungszeitraum", "Person / Organisation").
    private readonly reservationStepLabels: Partial<Record<BookingV2Step, string>> = {
        date: 'bookingV2.reservation.stepLabels.date',
        person: 'bookingV2.reservation.stepLabels.person',
    };

    readonly wizardSteps: Signal<readonly U2bWizardStep[]> = computed(() => {
        const reservation = this.facade.bookingType() === 'reservation';
        return this.facade.steps().map(id => {
            const def = this.stepDefinitions[id];
            const labelKey = reservation ? this.reservationStepLabels[id] : undefined;
            return labelKey ? { ...def, labelKey } : def;
        });
    });

    // Steps with an unresolved required field — drive the stepper's error markers.
    readonly invalidStepIds = computed<ReadonlySet<string>>(() =>
        new Set(this.commitService.missingRequirements().map(r => r.step)),
    );

    readonly saveFeedbackActive = signal<boolean>(false);

    readonly saveButtonLabelKey = computed<string>(() =>
        this.saveFeedbackActive() ? 'bookingV2.saveSuccessLabel' : 'common.save',
    );

    readonly saveButtonTooltipKey = computed<string>(() => {
        if (this.saveFeedbackActive()) {
            return 'bookingV2.saveSuccessLabel';
        }
        if (this.commitService.blockedByManualBoat()) {
            return 'bookingV2.saveDraftTooltip';
        }
        if (this.commitService.hasIncompleteSubscription()) {
            return 'bookingV2.saveSubscriptionTooltip';
        }
        return 'bookingV2.saveTooltipShortcut';
    });

    // Set when the user hits ✕ on a dirty dialog — mounts the discard confirmation.
    readonly closeRequested = signal<boolean>(false);
    // What to run once the guarded close goes through (e.g. "Beleg anzeigen").
    private afterCloseAction: (() => void) | null = null;

    // Post-save contract flow: the just-saved booking + whether its dialog is open.
    readonly contractDialogOpen = signal<boolean>(false);
    private readonly savedBooking = signal<BcmBooking | null>(null);

    // A create saved mid-wizard already exists — closing must still report it, or
    // callers treat the close as a cancel (no reload, map search mask not cleared).
    private createdBooking: BcmBooking | null = null;

    // Contract dialog inputs, mapped from the saved booking's real (persisted)
    // entities — the facade options may carry draft ids for freshly created records.
    readonly contractPersonOption = computed<BookingV2PersonOption | null>(() => {
        const b = this.savedBooking();
        if (!b) {
            return null;
        }
        const kind = this.facade.selectedPerson()?.kind;
        if (kind === 'company' && b.company) { return toCompanyOption(b.company); }
        if (kind === 'person' && b.person) { return toPersonOption(b.person); }
        if (b.company) { return toCompanyOption(b.company); }
        return b.person ? toPersonOption(b.person) : null;
    });
    readonly contractBoatOption = computed<BookingV2BoatOption | null>(() => {
        const b = this.savedBooking();
        return b?.boat ? toBoatOption(b.boat) : null;
    });
    readonly contractBerthOptions = computed<readonly BookingV2BerthOption[]>(() =>
        (this.savedBooking()?.berthAssignments ?? [])
            .map(a => a.berth)
            .filter((berth): berth is NonNullable<typeof berth> => !!berth)
            .map(toBerthOption),
    );
    readonly contractBookingId = computed(() => this.savedBooking()?.id ?? null);
    readonly contractFrom = computed(() => this.savedBooking()?.from ?? null);
    readonly contractTo = computed(() => this.savedBooking()?.to ?? null);

    private tourOverlayRef: OverlayRef | null = null;
    private draftGuardOverlayRef: OverlayRef | null = null;
    private confirmationOverlayRef: OverlayRef | null = null;
    private closeGuardOverlayRef: OverlayRef | null = null;
    private saveFeedbackTimer: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        // Edit: load the booking and seed every step. Otherwise apply the opener's
        // prefill synchronously, before any child step's ngOnInit runs.
        if (this.data?.bookingId != null) {
            this.loadExistingBooking(this.data.bookingId);
        } else {
            this.applyInitialData();
            // Baseline for the unsaved-changes guard: the opener's prefill is the
            // clean state, so closing an untouched (even prefilled) dialog never prompts.
            this.facade.capturePristineState();
        }

        // PrimeNG's DynamicDialog panel uses CSS transforms, which would
        // trap any `position: fixed` descendant inside the dialog. Render
        // the tour through a CDK overlay portaled to <body> so the modal
        // backdrop actually covers the viewport.
        effect(() => {
            if (this.facade.showTour()) {
                this.openTourOverlay();
            } else {
                this.disposeTourOverlay();
            }
        });

        // Same overlay-to-body approach for the unsaved-draft warning.
        effect(() => {
            if (this.facade.pendingDraftGuard()) {
                this.openDraftGuardOverlay();
            } else {
                this.disposeDraftGuardOverlay();
            }
        });

        this.facade.closeRequests$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(afterClose => this.close(afterClose));

        // ...and for the "discard the whole booking?" prompt shown when the user
        // closes a dialog that has unsaved changes.
        effect(() => {
            if (this.closeRequested()) {
                this.openCloseGuardOverlay();
            } else {
                this.disposeCloseGuardOverlay();
            }
        });

        // Create mode: when an existing boat is picked (manual selection or
        // prefill), propose its still-open standalone electric connections so the
        // user can take them over into the booking. Edit mode already loads the
        // booking's own meters, so it is skipped.
        effect(() => {
            const boat = this.facade.selectedBoat();
            // A real, persisted boat: any origin except the dimension-only
            // 'manual' draft or a not-yet-created 'new' boat (both lack a DB row).
            // Existing boats from prefill/search carry `origin: undefined`.
            const eligible = !this.facade.isEditMode()
                && !!boat && boat.id > 0
                && boat.origin !== 'manual' && boat.origin !== 'new';

            if (!eligible) {
                if (this.lastProposedBoatId !== null) {
                    this.dropProposals();
                    this.lastProposedBoatId = null;
                }
                return;
            }
            if (boat.id === this.lastProposedBoatId) {
                return;
            }
            this.lastProposedBoatId = boat.id;
            this.proposeOpenMeters(boat.id);
        });
    }

    // Fetch the boat's open standalone assignments and merge them into the power
    // tab. Additive — failures never block the booking.
    private proposeOpenMeters(boatId: number): void {
        this.boatApi.getOpenElectricMeterAssignments(boatId)
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: assignments => this.applyProposals(assignments),
                error: () => { /* proposal is optional — keep the wizard usable */ },
            });
    }

    private applyProposals(assignments: ElectricMeterAssignment[]): void {
        // Only OPEN (`!to`) and truly standalone (no berth assignment → not yet on
        // any booking) connections are proposals; the rest are out of scope.
        const proposals = assignments
            .filter(a => !a.to && !a.berthAssignment)
            .map(toProposedMeterAssignment);

        const kept = this.facade.electricMeterAssignments().filter(m => !m.reparent);
        const fresh: BookingV2ElectricMeterAssignment[] = [];
        for (const p of proposals) {
            // Skip meters the user already added manually, and de-dupe proposals.
            if (kept.some(m => m.meterId === p.meterId)) {
                continue;
            }
            if (fresh.some(m => m.meterId === p.meterId)) {
                continue;
            }
            fresh.push(p);
        }

        this.facade.electricMeterAssignments.set([...kept, ...fresh]);
        // Surface the proposal: the power tab only exists in advanced mode.
        if (fresh.length > 0) {
            this.facade.advancedMode.set(true);
        }
    }

    private dropProposals(): void {
        this.facade.electricMeterAssignments.update(list => list.filter(m => !m.reparent));
    }

    // Applies the opener's prefill (BookingV2DialogData) to the facade. All
    // fields are optional — each entry point fills only what it knows.
    private applyInitialData(): void {
        const d = this.data;
        if (!d) {
            return;
        }
        if (d.bookingType) {
            this.facade.setBookingType(d.bookingType);
        }
        if (d.dateRange) {
            this.facade.setDateRange(d.dateRange);
        } else {
            // Blank open (An-/Abreise widget, boat detail): seed today at the
            // marina's default check-in time (open end).
            this.facade.requestBlankDefaultTimes();
        }
        if (d.boat) {
            this.facade.selectedBoat.set(d.boat);
        }
        if (d.berths?.length) {
            // Preselected berths become occupancies seeded with the booking window.
            this.facade.seedOccupanciesFromBerths(d.berths);
        }
        if (d.person) {
            this.facade.selectedPerson.set(d.person);
            // An explicit booking person must never be overwritten by the
            // person step's one-shot boat-owner default.
            this.facade.ownerDefaultApplied.set(true);
        } else if (d.skipOwnerDefault) {
            this.facade.ownerDefaultApplied.set(true);
        }
        if (d.prefilledOrderId != null && d.prefilledOrderId > 0) {
            this.facade.selectedOrderId.set(d.prefilledOrderId);
        }
    }

    // Fetch the booking (backend eager-loads the full graph), seed the wizard;
    // on failure toast and close.
    private loadExistingBooking(bookingId: number): void {
        // The onboarding tour is a create-flow walkthrough — suppress it on edit
        // (set before the tour effect runs) so it never overlays/blocks the dialog.
        this.facade.showTour.set(false);
        this.loading.set(true);
        this.bookingsApi.getOne(bookingId)
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: booking => {
                    this.facade.seedFromExisting(mapBookingToWizardState(booking));
                    this.enrichRelationProducts();
                    // Loaded booking = clean baseline; async relation/service price
                    // enrichment is excluded from the signature, so it stays clean.
                    this.facade.capturePristineState();
                    this.loading.set(false);
                },
                error: () => {
                    this.notifications.showError(
                        TolgeeService.translate('bookingV2.notifications.loadError'),
                    );
                    this.loading.set(false);
                    this.dialogRef.close();
                },
            });
    }

    // Edit-load seeds the relation from the booking graph (no tax rate), so the
    // attached-products preview re-fetches the full relation for correct gross.
    private enrichRelationProducts(): void {
        const relation = this.facade.selectedRelation();
        if (!relation) {
            return;
        }
        this.tenantRelationsApi.getOne(relation.id)
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: full => this.facade.selectedRelation.update(r =>
                    r && r.id === full.id
                        ? {
                            ...r,
                            products: toRelationProducts(full),
                            payableOption: full.payableOption ?? null,
                            requiredEntities: relationRuleRequirements(full),
                        }
                        : r,
                ),
                error: () => { /* preview enrichment is optional */ },
            });
    }

    close(afterClose?: () => void): void {
        // An in-progress advanced-tab entry OR any filled/changed wizard field must
        // be confirmed before the whole booking draft is discarded. A clean dialog
        // closes straight away (mirrors the advanced-tab guard's clean = proceed).
        if (this.facade.advancedDraftDirty() || this.facade.isDialogDirty()) {
            this.afterCloseAction = afterClose ?? null;
            this.closeRequested.set(true);
            return;
        }
        this.closeThen(afterClose);
    }

    // Queued after close so a caller's onClose navigation (e.g. the /bookings/:id
    // deep link) cannot override the follow-up navigation (#6917).
    private closeThen(afterClose?: (() => void) | null): void {
        this.afterCloseAction = null;
        this.dialogRef.close(this.createdBooking ?? undefined);
        if (afterClose) {
            queueMicrotask(afterClose);
        }
    }

    next(): void {
        this.dismissOrphanedStepOverlays();
        this.facade.advanceToNextStep();
    }

    prev(): void {
        this.dismissOrphanedStepOverlays();
        this.facade.guardAdvancedDraft(() => this.facade.goToPreviousStep());
    }

    goToRequirementStep(step: BookingV2Step): void {
        this.dismissOrphanedStepOverlays();
        this.facade.guardAdvancedDraft(() => this.facade.goToStep(step));
    }

    @HostListener('document:keydown', ['$event'])
    onDocumentKeydown(event: KeyboardEvent): void {
        if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
            event.preventDefault();
            this.save();
        }
    }

    onStepSelected(stepId: string): void {
        const step = stepId as BookingV2Step;
        if (this.facade.steps().includes(step)) {
            this.dismissOrphanedStepOverlays();
            this.facade.guardAdvancedDraft(() => this.facade.goToStep(step));
        }
    }

    /**
     * PrimeNG widgets with `appendTo="body"` (date pickers, selects) can leave
     * invisible overlay nodes behind when a wizard step is destroyed — those
     * panels capture pointer events and make the dialog feel frozen.
     */
    private dismissOrphanedStepOverlays(): void {
        const selectors = [
            '.p-datepicker-panel',
            '.p-select-overlay',
            '.p-select-list-container',
            '.p-connected-overlay',
        ];
        for (const selector of selectors) {
            document.querySelectorAll(selector).forEach(el => el.remove());
        }
    }

    save(): void {
        if (!this.commitService.canCommit() || this.commitService.saving()) {
            return;
        }
        // Edit reuses no confirmation prompt — the backend only sends a
        // Buchungsbestätigung on create (mirrors the legacy dialog).
        if (this.facade.isEditMode()) {
            this.commitBooking(false);
            return;
        }
        // Create: ask whether to send a Buchungsbestätigung (Yes / No / Cancel).
        this.openConfirmationOverlay();
    }

    private commitBooking(sendConfirmation: boolean): void {
        // Errors surface as a toast inside the service; keep the dialog open so
        // the user can retry.
        this.commitService.commit(sendConfirmation).subscribe({
            next: booking => this.finishSave(booking),
            error: () => { /* handled by the commit service */ },
        });
    }

    // Post-save contract generator (armed via the summary toggle) takes precedence:
    // keep the wizard mounted behind the contract dialog and finish the save
    // (Beleg + close) only once the contract dialog closes.
    private finishSave(booking: BcmBooking): void {
        if (this.facade.contractIntent().create) {
            this.savedBooking.set(booking);
            this.contractDialogOpen.set(true);
            return;
        }
        if (this.facade.belegIntent().create) {
            this.completeSave(booking);
            return;
        }
        // Create: mid-wizard save refreshes state and keeps the dialog open until
        // the summary step. Edit: Speichern finishes the session (e.g. Rechnungsversand
        // after documents) — do not jump back to the summary (#7257).
        const stayOpenAfterSave = !this.facade.isEditMode() && !this.facade.isLastStep();
        if (stayOpenAfterSave) {
            this.createdBooking = booking;
            this.facade.seedFromExisting(mapBookingToWizardState(booking));
            this.enrichRelationProducts();
            this.facade.capturePristineState();
            this.showSaveSuccessFeedback();
            return;
        }
        this.completeSave(booking);
    }

    private showSaveSuccessFeedback(): void {
        this.saveFeedbackActive.set(true);
        if (this.saveFeedbackTimer) {
            clearTimeout(this.saveFeedbackTimer);
        }
        this.saveFeedbackTimer = setTimeout(() => this.saveFeedbackActive.set(false), 2000);
    }

    onContractDialogVisibleChange(open: boolean): void {
        if (open) {
            return;
        }
        this.contractDialogOpen.set(false);
        const booking = this.savedBooking();
        this.savedBooking.set(null);
        if (booking) {
            this.completeSave(booking);
        }
    }

    // On success either close, or — if a Beleg was armed in the Rechnungsversand
    // step — close the wizard first, then open the prefilled Beleg (same tab).
    // Capture facade state before close; the PrimeNG overlay blocks navigation
    // if the invoice form opens while the dialog is still mounted (#6917).
    private completeSave(booking: BcmBooking): void {
        if (!this.facade.belegIntent().create) {
            this.dialogRef.close(booking);
            return;
        }
        const kind = this.facade.belegIntent().kind;
        const recipientKind: BookingV2PersonKind = this.facade.selectedPerson()?.kind ?? 'person';
        const belegSelection = this.facade.belegSelection();
        const depositOnBeleg = belegSelection.some(i => i.key === 'deposit');
        const depositDueDate = depositOnBeleg && this.facade.bookingType() === 'reservation' && this.facade.depositRequired()
            ? (this.facade.depositDueDate() ?? this.facade.reservationValidUntil())
            : null;

        this.bookingsApi.getPositions(booking.id)
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: positions => {
                    const selected = selectBelegPositions(positions, belegSelection);
                    this.dialogRef.close(booking);
                    if (selected.length === 0) {
                        this.notifications.showInfo(
                            TolgeeService.translate('bookingV2.rechnungsversand.noOpenPositions'),
                        );
                        return;
                    }
                    const recipientId = booking.person?.id ?? booking.company?.id ?? this.facade.selectedPersonId();
                    queueMicrotask(() => {
                        this.openBeleg(kind, selected, booking, recipientKind, recipientId, depositDueDate);
                    });
                },
                error: () => this.dialogRef.close(booking),
            });
    }

    // Rechnung → redirect (same tab) to the prefilled invoice form; Quittung →
    // the pay-by-cash dialog. Positions belong to the recipient, so the invoice
    // form resolves them from the recipient's open positions.
    private openBeleg(
        kind: 'invoice' | 'receipt',
        positions: { id: number }[],
        booking: BcmBooking,
        recipientKind: BookingV2PersonKind,
        recipientId: number | null | undefined,
        depositDueDate: Date | null,
    ): void {
        if (kind === 'receipt') {
            this.matDialog.open(PayByCashDialogComponent, {
                data: {
                    positions,
                    person: recipientKind === 'person' ? booking.person : undefined,
                    company: recipientKind === 'company' ? booking.company : undefined,
                },
                disableClose: true,
            });
            return;
        }
        const navParams = buildNewInvoiceNavigationParams(
            positions,
            recipientKind === 'company'
                ? { companyId: recipientId }
                : { personId: recipientId },
            booking.id,
        );
        if (depositDueDate) {
            navParams.dueDate = depositDueDate.toISOString();
        }
        void this.bcmNavigation.navigate(['accounting', 'invoices', 'new', navParams]);
    }

    ngOnDestroy(): void {
        if (this.saveFeedbackTimer) {
            clearTimeout(this.saveFeedbackTimer);
        }
        this.disposeTourOverlay();
        this.disposeDraftGuardOverlay();
        this.disposeConfirmationOverlay();
        this.disposeCloseGuardOverlay();
    }

    // "Discard the whole booking?" — shown when the user closes a dialog carrying
    // unsaved changes. Confirm → close, cancel → keep editing. Same overlay-to-body
    // approach as the draft guard (the PrimeNG panel would trap a fixed child).
    private openCloseGuardOverlay(): void {
        if (this.closeGuardOverlayRef) {
            return;
        }
        const overlayRef = this.overlay.create({
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            positionStrategy: this.overlay.position().global(),
        });
        const compRef = overlayRef.attach(new ComponentPortal(U2bConfirmDialogComponent));
        compRef.setInput('severity', 'danger');
        compRef.setInput('icon', 'warning');
        compRef.setInput('titleKey', 'bookingV2.close.unsavedChanges.title');
        compRef.setInput('messageKey', 'bookingV2.close.unsavedChanges.body');
        compRef.setInput('confirmKey', 'bookingV2.close.unsavedChanges.discard');
        compRef.setInput('cancelKey', 'common.cancel');
        compRef.instance.confirmed.subscribe(() => this.closeThen(this.afterCloseAction));
        compRef.instance.cancelled.subscribe(() => {
            this.afterCloseAction = null;
            this.closeRequested.set(false);
        });
        this.closeGuardOverlayRef = overlayRef;
    }

    private disposeCloseGuardOverlay(): void {
        if (!this.closeGuardOverlayRef) {
            return;
        }
        this.closeGuardOverlayRef.dispose();
        this.closeGuardOverlayRef = null;
    }

    // "Send a Buchungsbestätigung?" — same overlay-to-body approach as the draft
    // guard. Confirm → send, decline → save without sending, cancel → abort.
    private openConfirmationOverlay(): void {
        if (this.confirmationOverlayRef) {
            return;
        }
        const overlayRef = this.overlay.create({
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            positionStrategy: this.overlay.position().global(),
        });
        const compRef = overlayRef.attach(new ComponentPortal(U2bConfirmDialogComponent));
        compRef.setInput('severity', 'primary');
        compRef.setInput('icon', 'mark_email_read');
        compRef.setInput('titleKey', 'bookingV2.confirmation.title');
        compRef.setInput('messageKey', 'bookingV2.confirmation.body');
        compRef.setInput('confirmKey', 'bookingV2.confirmation.send');
        compRef.setInput('declineKey', 'bookingV2.confirmation.dontSend');
        compRef.setInput('cancelKey', 'common.cancel');
        compRef.instance.confirmed.subscribe(() => { this.disposeConfirmationOverlay(); this.commitBooking(true); });
        compRef.instance.declined.subscribe(() => { this.disposeConfirmationOverlay(); this.commitBooking(false); });
        compRef.instance.cancelled.subscribe(() => this.disposeConfirmationOverlay());
        this.confirmationOverlayRef = overlayRef;
    }

    private disposeConfirmationOverlay(): void {
        if (!this.confirmationOverlayRef) {
            return;
        }
        this.confirmationOverlayRef.dispose();
        this.confirmationOverlayRef = null;
    }

    private openTourOverlay(): void {
        if (this.tourOverlayRef) {
            return;
        }
        const overlayRef = this.overlay.create({
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            positionStrategy: this.overlay.position().global(),
            panelClass: 'booking-v2-tour-overlay-pane',
        });
        const portal = new ComponentPortal(OnboardingTourComponent);
        const compRef = overlayRef.attach(portal);
        compRef.instance.closeTour.subscribe(() => this.facade.closeTour());
        this.tourOverlayRef = overlayRef;
    }

    private disposeTourOverlay(): void {
        if (!this.tourOverlayRef) {
            return;
        }
        this.tourOverlayRef.dispose();
        this.tourOverlayRef = null;
    }

    private openDraftGuardOverlay(): void {
        if (this.draftGuardOverlayRef) {
            return;
        }
        const overlayRef = this.overlay.create({
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.noop(),
            positionStrategy: this.overlay.position().global(),
        });
        const compRef = overlayRef.attach(new ComponentPortal(U2bConfirmDialogComponent));
        compRef.setInput('severity', 'danger');
        compRef.setInput('icon', 'warning');
        compRef.setInput('titleKey', 'bookingV2.advanced.unsavedDraft.title');
        compRef.setInput('messageKey', 'bookingV2.advanced.unsavedDraft.body');
        compRef.setInput('confirmKey', 'bookingV2.advanced.unsavedDraft.discard');
        compRef.setInput('cancelKey', 'common.cancel');
        compRef.instance.confirmed.subscribe(() => this.facade.resolveDraftGuard(true));
        compRef.instance.cancelled.subscribe(() => this.facade.resolveDraftGuard(false));
        this.draftGuardOverlayRef = overlayRef;
    }

    private disposeDraftGuardOverlay(): void {
        if (!this.draftGuardOverlayRef) {
            return;
        }
        this.draftGuardOverlayRef.dispose();
        this.draftGuardOverlayRef = null;
    }
}
